import {AxiosError} from "axios";

import {toast} from "../hooks/use-toast";
import {ApiErrorResponse} from "../types";
import {ERROR_MESSAGES} from "../utils/constants";

export function getAxiosErrorMessage(error: unknown): [string, string[]] {
  if (!(error instanceof AxiosError)) {
    return [error instanceof Error ? error.message : "Something went wrong", []];
  }

  if (!error.response) {
    return [error.message, []];
  }

  const apiErrorResponse = error.response.data as ApiErrorResponse & {
    errors?: string[] | Record<string, string[]>;
    data?: {errors?: string[] | Record<string, string[]>};
  };
  const raw =
    apiErrorResponse.errors ??
    (apiErrorResponse.data?.errors as string[] | Record<string, string[]> | undefined);
  const errors: string[] = Array.isArray(raw)
    ? raw.map(String)
    : raw && typeof raw === "object"
      ? (Object.values(raw) as string[][]).flat().map(String)
      : [];

  const apiMessage = apiErrorResponse.message?.trim();
  if (apiMessage) {
    return [apiMessage, errors];
  }

  if (error.response.status >= 401) {
    return [
      ERROR_MESSAGES[error.response.status as keyof typeof ERROR_MESSAGES] ??
        "Something went wrong",
      errors,
    ];
  }

  return [apiErrorResponse.message, errors];
}

export function errorHandler(error: unknown) {
  const [message, errors] = getAxiosErrorMessage(error);

  if (errors?.length > 0) {
    toast.error(() => errors.map((err, index) => <p key={`toast-error_${index}`}>{err}</p>));
  } else if (message) {
    toast.error(message);
  }
}

export function throwOnErrorHandler(error: AxiosError | Error) {
  const isFormValidationError =
    error instanceof Error && (error.cause as any)?.origin === "form_validation";
  const isLocationRequiredError =
    error instanceof AxiosError && error.response?.data?.code === "location_required";
  const isLocationDisabledError =
    error.cause instanceof GeolocationPositionError && error.cause.code === 1;

  return isFormValidationError || isLocationRequiredError || isLocationDisabledError;
}
